import type { TypographyProps } from "@mui/material";
import { Typography } from "@mui/material";
import type { PropsWithChildren } from "react";
import useToggleState from "../hooks/useToggleState";
import useVerticalOverflowDetection from "../hooks/useVerticalOverflowDetection";
import ShowMoreLessButton from "./ShowMoreLessButton";

interface Props {
  maxLines: number;
  typographyProps?: TypographyProps;
  expandButtonText?: string;
  collapseButtonText?: string;
}

const ExpandableTypography = ({
  maxLines,
  typographyProps,
  expandButtonText,
  collapseButtonText,
  children,
}: PropsWithChildren<Props>) => {
  const [expanded, toggleExpanded] = useToggleState(false);
  const { hasOverflow, containerRef } = useVerticalOverflowDetection([maxLines, children], { disabled: expanded });

  const { sx, ...props } = typographyProps ?? {};

  return (
    <>
      <Typography
        {...props}
        ref={containerRef}
        sx={
          expanded
            ? { ...sx }
            : {
                overflow: "hidden",
                textOverflow: "ellipsis",
                WebkitLineClamp: maxLines,
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                ...sx,
              }
        }
      >
        {children}
      </Typography>

      {(hasOverflow || expanded) && (
        <ShowMoreLessButton
          expanded={expanded}
          onClick={toggleExpanded}
          expandButtonText={expandButtonText}
          collapseButtonText={collapseButtonText}
        />
      )}
    </>
  );
};

export default ExpandableTypography;
